// Per-panel state machine (Story 2.13).
//
// Cada hero panel posee SU PROPIO error — el design system no expone
// un global toast (UX-DR32). Un panel puede estar en `error` mientras
// los otros tres siguen pollando happy-path.
//
// Estados:
//   loading      — first-fetch in flight, sin data prev.
//   data         — happy path; data presente.
//   refreshing   — poll en background; mostramos last-good data
//                  pero el indicador "last updated" permite ver el
//                  staleness (Story 2.14).
//   error        — Story 2.14 garantiza que llega después de 3 fallos
//                  consecutivos; este composable solo expone el setter
//                  y la transición.
//
// La transición `data → error` es decisión del caller (usePoll), no
// de este composable.

import { ref } from 'vue';

const VALID_STATES = new Set(['loading', 'data', 'empty', 'refreshing', 'error']);

export function usePanelState(initial = 'loading') {
  const state = ref(VALID_STATES.has(initial) ? initial : 'loading');
  const errorMessage = ref('');
  const errorDetail = ref('');
  const retrying = ref(false);
  let retryHandler = null;

  function setState(next, opts = {}) {
    if (!VALID_STATES.has(next)) return;
    state.value = next;
    if (next === 'error') {
      errorMessage.value = opts.message ?? '';
      errorDetail.value = opts.detail ?? '';
    } else if (next === 'data') {
      errorMessage.value = '';
      errorDetail.value = '';
    }
    retrying.value = false;
  }

  function setRetryHandler(fn) {
    retryHandler = typeof fn === 'function' ? fn : null;
  }

  async function retry() {
    if (!retryHandler) return;
    retrying.value = true;
    try {
      await retryHandler();
    } finally {
      retrying.value = false;
    }
  }

  return { state, errorMessage, errorDetail, retrying, setState, setRetryHandler, retry };
}
